/* eslint-disable react/no-unknown-property */
import { Application, extend, type ApplicationRef } from '@pixi/react'
import {
  AnimatedSprite as PixiAnimatedSprite,
  Assets,
  Spritesheet,
  Texture,
} from 'pixi.js'
import { useCallback, useEffect, useRef, useState } from 'react'

extend({ AnimatedSprite: PixiAnimatedSprite })

const SPRITESHEET_PATH = '/assets/lastSpiral_box_pixi/1.json'
const TARGET_FPS = 24
const ANIMATION_SPEED = TARGET_FPS / 60

const loadAnimationTextures = async (): Promise<Texture[]> => {
  const sheet = Assets.cache.has(SPRITESHEET_PATH)
    ? (Assets.cache.get(SPRITESHEET_PATH) as Spritesheet)
    : await Assets.load<Spritesheet>(SPRITESHEET_PATH)
  await sheet.parse()
  const frames = (sheet.data.animations?.['0'] as string[]) ?? []
  return frames
    .map((name) => sheet.textures[name])
    .filter((t): t is Texture => Boolean(t))
}

type Props = { className?: string }

export const BoxPixiAnimation = ({ className }: Props) => {
  const appRef = useRef<ApplicationRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const spriteRef = useRef<PixiAnimatedSprite | null>(null)
  const [textures, setTextures] = useState<Texture[]>([])
  const [dims, setDims] = useState({ width: 150, height: 150 })

  useEffect(() => {
    let alive = true
    loadAnimationTextures()
      .then((tex) => {
        if (alive) setTextures(tex)
      })
      .catch((err) => console.error('BoxPixiAnimation: failed to load', err))
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      const rect = el.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        setDims({ width: rect.width, height: rect.height })
      }
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const setSpriteRef = useCallback((sprite: PixiAnimatedSprite | null) => {
    spriteRef.current = sprite
    if (sprite) {
      sprite.loop = true
      sprite.animationSpeed = ANIMATION_SPEED
      requestAnimationFrame(() => sprite.play())
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: '100%', height: '100%' }}
    >
      {textures.length > 0 && (
        <Application
          ref={appRef}
          width={dims.width}
          height={dims.height}
          backgroundAlpha={0}
          antialias
          autoDensity
          resolution={window.devicePixelRatio || 2}
        >
          <pixiAnimatedSprite
            ref={setSpriteRef}
            textures={textures}
            anchor={0.5}
            x={dims.width / 2}
            y={dims.height / 2}
            width={dims.width}
            height={dims.height}
            animationSpeed={ANIMATION_SPEED}
            loop
            eventMode="none"
          />
        </Application>
      )}
    </div>
  )
}
